import React, { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PreferencesTree from '../PreferencesTree';
import {
  useLoadNutsCodes,
  useLoadMp,
  getUpdateCacheOnUpdateMpNutsCodes,
  useUpdateMpNutsCodes,
  useNutsFilter
} from 'src/models/matchingProfiles/hooks';
import {
  getUpdateCacheOnUpdateCompanyNutsCodes,
  useUpdateCompanyNutsCodes,
  useLoadCompanyNuts
} from 'src/models/admin/CompanyUsers/hooks';
import { notification, Skeleton } from 'src/common';
import styles from './index.module.scss';
import { toPreferencesTree } from 'src/models/matchingProfiles/helpers';

interface IProps {
  disabled: boolean;
  companyNutsCodeView?: boolean;
  companyId?: string;
}

export const NutsCodesTree: FC<IProps> = props => {
  const { disabled } = props;
  const companyNutsCodeView = props.companyNutsCodeView ?? false;
  const companyId = props.companyId;
  const { t } = useTranslation();
  const { data: nutsCodesData } = useLoadNutsCodes();
  const nutCodes = nutsCodesData?.searchNutCodes;

  const { data: mp } = useLoadMp(undefined, companyNutsCodeView);
  const matchingNutCodes = mp?.nutsCodes;
  const matchingNutCodesExact = mp?.nutsCodesExact;

  const { data: compNutData } = useLoadCompanyNuts(companyId, !companyNutsCodeView);
  const companyNutFilter = compNutData?.getCompanyNutsFilter;
  const compNutCodes = compNutData?.getCompanyNutsFilter.nutsCodes;
  const compNutCodesExact = compNutData?.getCompanyNutsFilter.nutsCodesExact;

  const [updateNutCodes, { loading: isUpdating }] = useUpdateMpNutsCodes();
  const [updateCompanyNutCodes, { loading: isUpdatingCompanyNut, error: updatingCompanyNutErrors }] =
    useUpdateCompanyNutsCodes();

  const { data: nutsFilter, loading: nutsFilterLoading } = useNutsFilter(companyNutsCodeView);

  const isReady = nutCodes && (companyNutsCodeView || !nutsFilterLoading);

  const availableNodes = useMemo(
    () => (nutCodes ? toPreferencesTree('nutsCodes', nutCodes, t, nutsFilter?.getNutsFilter) : []),
    [nutCodes, nutsFilter, t]
  );

  const updateCheckedKeys = useCallback(
    (nutsCodes: string[], nutsCodesExact: string[]) => {
      mp &&
        updateNutCodes({
          variables: {
            nutsCodes,
            nutsCodesExact,
            id: mp.id
          },
          update: getUpdateCacheOnUpdateMpNutsCodes(mp.id),
          optimisticResponse: {
            __typename: 'Mutation',
            updateMatchingProfile: {
              __typename: 'MatchingProfile',
              ...mp,
              nutsCodes,
              nutsCodesExact
            }
          }
        }).catch(() => {
          notification.error({
            message: t('Common.unknownError'),
            description: t('Common.unknownErrorDesc')
          });
        });
    },
    [mp, t, updateNutCodes]
  );

  const updateCheckedCompanyKeys = useCallback(
    (nutsCodes: string[], nutsCodesExact: string[]) => {
      companyNutFilter &&
        updateCompanyNutCodes({
          variables: {
            companyId,
            nutsCodes,
            nutsCodesExact
          },
          update: getUpdateCacheOnUpdateCompanyNutsCodes(companyNutFilter.id),
          optimisticResponse: {
            __typename: 'Mutation',
            updateNutsFilter: {
              __typename: 'NutsFilter',
              ...companyNutFilter,
              nutsCodes,
              nutsCodesExact
            }
          }
        });
    },
    [companyId, companyNutFilter, updateCompanyNutCodes]
  );

  return (
    <>
      {isReady ? (
        <PreferencesTree
          className={companyNutsCodeView ? styles.scrollablePreferencesTree : ''}
          availableNodes={availableNodes}
          checkedKeys={companyNutsCodeView ? compNutCodes ?? [] : matchingNutCodes ?? []}
          checkedKeysExact={companyNutsCodeView ? compNutCodesExact ?? [] : matchingNutCodesExact ?? []}
          updateCheckedKeys={companyNutsCodeView ? updateCheckedCompanyKeys : updateCheckedKeys}
          updateCheckedKeysError={companyNutsCodeView ? updatingCompanyNutErrors : undefined}
          checkedKeysUpdating={companyNutsCodeView ? isUpdatingCompanyNut : isUpdating}
          cardDescription={t(
            companyNutsCodeView ? 'Tenders.companyNutCodesDescription' : 'Tenders.nutCodesDescription'
          )}
          updatingErrorTitle={t('Tenders.hasNotUpdated', { itemName: t('Tenders.nutsCodes') })}
          disabled={disabled}
          type={'nutsCodes'}
          showKey
          checkStrictly
        />
      ) : (
        <Skeleton loading active />
      )}
    </>
  );
};

export default NutsCodesTree;
